const STORAGE_DECIMAL = '.';
const DEFAULT_DECIMAL = '.';

export interface NumberSeparatorOptions {
    groupSeparator?: string;
    decimalSeparator?: string;
}

/**
 * Apply group_separator on the integer part and decimal_separator on the decimal point.
 * Input is the canonical stored value: '.' as decimal, no grouping.
 * When groupSeparator is undefined or empty → no grouping is applied.
 * When decimalSeparator is undefined → '.' is used.
 */
export const formatNumberForDisplay = (
    rawValue: string | number | undefined | null,
    options: NumberSeparatorOptions = {}
): string => {
    if (rawValue === undefined || rawValue === null || rawValue === '') return '';

    const {groupSeparator, decimalSeparator = DEFAULT_DECIMAL} = options;

    let strVal = String(rawValue);
    let sign = '';
    if (strVal.startsWith('-')) {
        sign = '-';
        strVal = strVal.slice(1);
    }

    const parts = strVal.split(STORAGE_DECIMAL);
    let intPart = parts[0];
    const decPart = parts.length > 1 ? parts.slice(1).join('') : undefined;

    if (groupSeparator !== undefined && groupSeparator !== '') {
        intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator);
    }

    return decPart !== undefined
        ? `${sign}${intPart}${decimalSeparator}${decPart}`
        : `${sign}${intPart}`;
};

/**
 * Reverse of formatNumberForDisplay: strip group separators and normalize
 * the decimal separator back to '.' for storage.
 * Permissive: accepts both ',' and '.' as decimal regardless of decimalSeparator.
 * May return a partial string (e.g. '12.', '-') during typing.
 */
export const normalizeFromDisplay = (
    displayValue: string,
    options: NumberSeparatorOptions = {}
): string => {
    if (displayValue === undefined || displayValue === null || displayValue === '') return '';

    const {groupSeparator, decimalSeparator = DEFAULT_DECIMAL} = options;

    let s = String(displayValue);

    if (groupSeparator !== undefined && groupSeparator !== '') {
        s = s.split(groupSeparator).join('');
    }

    if (decimalSeparator !== STORAGE_DECIMAL) {
        s = s.split(decimalSeparator).join(STORAGE_DECIMAL);
    }

    const firstDot = s.indexOf(STORAGE_DECIMAL);
    if (firstDot !== -1) {
        s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).split(STORAGE_DECIMAL).join('');
    }

    return s;
};

/**
 * Keep the caret stable across a format/strip cycle by anchoring on the count
 * of significant characters (non-group-separator) before the caret.
 */
export const computeCaretPosition = (
    oldDisplay: string,
    newDisplay: string,
    oldCaret: number,
    options: NumberSeparatorOptions = {}
): number => {
    const {groupSeparator} = options;
    if (groupSeparator === undefined || groupSeparator === '') return oldCaret;

    let significantBefore = 0;
    for (let i = 0; i < oldCaret && i < oldDisplay.length; i++) {
        if (oldDisplay[i] !== groupSeparator) significantBefore++;
    }

    if (significantBefore === 0) return 0;

    let seen = 0;
    for (let i = 0; i < newDisplay.length; i++) {
        if (newDisplay[i] !== groupSeparator) {
            seen++;
            if (seen === significantBefore) return i + 1;
        }
    }
    return newDisplay.length;
};
